import {
  Box,
  BoxProps,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { LSTIconMapping, getLSTOptionsForChain } from "@constants/common";
import { FaChevronDown } from "react-icons/fa6";
import { useSelector } from "react-redux";

import useUserType from "@hooks/useUserType";
import { useEffect } from "react";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";

const MenuItemProps = {
  p: "6px 8px",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: "400",
};

const SelectedMenuItemProps = {
  ...MenuItemProps,
  bg: "#F5F5F7",
  color: "#142528",
  fontWeight: "600",
  lineHeight: "20px",
  letterSpacing: "-0.70px",
};

interface StableTokenSelectProps extends BoxProps {
  disabled?: boolean;
  selected: string;
  onTokenSelect?: (selected: string) => void;
  tokenBgColor?: string;
  isStableVaultDeposit?: boolean;
}

const StableTokenSelect = ({
  selected,
  onTokenSelect,
  disabled = false,
  tokenBgColor,
  isStableVaultDeposit = false,
  ...rest
}: StableTokenSelectProps) => {
  const { selectedERC20StableVault } = useSelector((state: any) => state.user);
  const { chain } = useDappChain();
  const { stableVaultConfig } = useDappConfig();

  const selectedAsset = selected || selectedERC20StableVault;

  const { isStaderLedgerUser } = useUserType();

  let options = [];

  if (isStableVaultDeposit && stableVaultConfig?.tokens) {
    options = Object.keys(stableVaultConfig.tokens).map((tokenKey) => {
      const token = stableVaultConfig.tokens[tokenKey];
      return {
        key: token.symbol,
        title: token.symbol,
        priority: token.priority || 0,
        isDisabled: false,
      };
    });
  } else {
    options = getLSTOptionsForChain(chain.id, false, isStaderLedgerUser, true);
  }

  const filteredOptions = isStableVaultDeposit
    ? options
    : options.filter((option) => option.key !== "sbUSD");

  const sortKey = "priority";

  const sortedOptions = filteredOptions.sort(
    (a: any, b: any) => (a?.[sortKey] || 0) - (b?.[sortKey] || 0)
  );

  useEffect(() => {
    if (
      sortedOptions.length > 0 &&
      !sortedOptions.find((o) => o.key === selectedAsset)
    ) {
      onTokenSelect?.(sortedOptions[0].key);
    }
  }, [sortedOptions, selectedAsset, onTokenSelect]);

  const bgColor = tokenBgColor || "#B6DFDF";

  return (
    <Flex w="100%" mb="2.5rem" {...rest} position={"unset"}>
      <Menu placement={"bottom-end"}>
        <MenuButton
          as={Button}
          rightIcon={
            !disabled ? <FaChevronDown fontSize={"12px"} /> : undefined
          }
          isDisabled={disabled}
          w="100%"
          h="36px"
          textAlign="left"
          className="menuButtonStake"
          p={{ base: "6px", md: "6px 8px 6px 6px" }}
          borderRadius="6px"
          bg={bgColor}
          _hover={{
            bg: { bgColor },
          }}
          _active={{
            bg: { bgColor },
          }}
          _disabled={{
            bg: { bgColor },
          }}
          fontWeight="600"
          fontSize={{ base: "14px", md: "16px" }}
          letterSpacing="-0.70px"
        >
          <Flex alignItems="center" gap="8px">
            {LSTIconMapping[selectedAsset] && (
              <BaseImage
                width={18}
                height={18}
                alt="lst"
                src={LSTIconMapping[selectedAsset]}
              />
            )}
            <Flex alignItems="center" justify="space-between" flex={1} gap={2}>
              <Box flex={1}>{selectedAsset}</Box>
            </Flex>
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList
            transformOrigin="top !important"
            w={{
              base: "340px",
              sm: "100%",
            }}
            p="8px"
            border="1px solid #AEDFCA"
            bg="backgroundCard"
            fontSize="14px"
            fontWeight="400"
          >
            {sortedOptions.map((option, index) => {
              if (option.key === "divider") {
                return <MenuDivider my="12px" key={index} />;
              } else {
                const props =
                  option.key === selectedAsset
                    ? { ...SelectedMenuItemProps }
                    : { ...MenuItemProps };
                return (
                  <MenuItem
                    {...props}
                    isDisabled={option?.isDisabled}
                    _disabled={{
                      cursor: "not-allowed",
                      color: "textTertiary",
                    }}
                    padding={2}
                    marginBottom={1}
                    key={index}
                    onClick={() => onTokenSelect?.(option.key)}
                  >
                    <Flex
                      justifyContent="space-between"
                      w="100%"
                      alignItems="center"
                    >
                      <Flex alignItems="center" gap="8px" flex={1}>
                        {LSTIconMapping[option.key] && (
                          <BaseImage
                            width={24}
                            height={24}
                            alt="lst"
                            src={LSTIconMapping[option.key]}
                          />
                        )}
                        <Flex
                          justify="space-between"
                          flex={1}
                          alignItems="center"
                          gap={2}
                        >
                          <Box flex={1} fontSize={18}>
                            {option.title}
                          </Box>
                        </Flex>
                      </Flex>
                    </Flex>
                  </MenuItem>
                );
              }
            })}
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
};

export default StableTokenSelect;
